import { Divider } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from 'common/src/exceptions/ErrorHandling';
import { IFacilityProject, IProject } from 'common/src/types/interfaces';

import { addProject, updateProject } from 'portal/src/api/ProjectsService';
import useAuth from 'portal/src/auth/useAuth';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { formControlErrors } from 'portal/src/components/common/forms/formControlErrors';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { ValidationError } from 'portal/src/utils/ValidationError';
import { ValidateGuard } from 'portal/src/validators/validateGuard';

import { defaultValues, getProjectFields } from './ProjectForm.fields';
import { ProjectFormValidators } from './ProjectForm.validators';

import useSnackbarHandler from './../../../../utils/Snackbar/useSnackbarHandler';
import InitialFacilities from './Facilities/InitialFacilities';

interface FormComponentProps {
  entity?: Partial<IProject>;
  handleBackAction: () => void;
}

const ProjectForm = ({ entity = {}, handleBackAction }: FormComponentProps) => {
  const snackbarHandler = useSnackbarHandler();
  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
    setError,
  } = useForm({ defaultValues: defaultValues(entity) });
  const { getUser } = useAuth();
  const { organizationId } = getUser();
  const [initialFacilities, setInitialFacilities] = useState<
    IFacilityProject[]
  >(entity.facilities ?? []);
  const [facilitiesChanged, setFacilitiesChanged] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationKey: ['projectForm'],
    mutationFn: async (payload) => {
      const getCompleteValues = () => payload;
      const projectFormValidator = new ProjectFormValidators(getCompleteValues);
      await ValidateGuard.validate(
        projectFormValidator.getFacilitiesValidator(),
      );

      const action = entity.id ? update : save;
      await action(payload);
    },
    onSuccess: () => {
      const {
        PROJECT: { CREATED: save, UPDATED: update },
      } = MESSAGES;
      snackbarHandler.success(entity.id ? update : save);
      handleBackAction();
    },
    onError: (err: any) => {
      if (err instanceof ValidationError) {
        snackbarHandler.error(err.errors.join('; \n'));
      } else if (ApiError.compatible(err)) {
        formControlErrors({ getValues, setError }, err);
      } else {
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      }
    },
  });

  const projectFormFieldsConfig = getProjectFields();

  async function save(payload) {
    return await addProject({ ...payload, organizationId });
  }

  async function update(payload) {
    return await updateProject(entity.id, payload);
  }

  const handleFacilitiesChange = (facilities: IFacilityProject[]) => {
    setInitialFacilities(facilities);
    if (!facilitiesChanged) setFacilitiesChanged(true);
  };

  return (
    <FormContainer
      afterMainFields={
        <>
          <Divider>Facilities</Divider>
          <InitialFacilities
            data={initialFacilities}
            onDataChange={handleFacilitiesChange}
          />
        </>
      }
      control={control}
      isDirty={isDirty || facilitiesChanged}
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) =>
        mutate(Object.assign(data, { facilities: initialFacilities })),
      )}
      header="Project Information"
      isNew={!entity.id}
      isPending={isPending}
      mainFieldsConfig={projectFormFieldsConfig}
    />
  );
};

export default ProjectForm;
